import React from 'react';

const TYPE_ICONS = {
  rate_hike: '\u2191',
  promo_start: '\uD83C\uDFF7\uFE0F',
  promo_end: '\u21A9\uFE0F',
  service_change: '\u2699\uFE0F',
};

const TYPE_COLORS = {
  rate_hike: '#dc2626',
  promo_start: '#16a34a',
  promo_end: '#f59e0b',
  service_change: '#2563eb',
};

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '24px',
  },
  timeline: {
    position: 'relative',
    paddingLeft: '0',
  },
  line: {
    position: 'absolute',
    left: '50%',
    top: '0',
    bottom: '0',
    width: '2px',
    background: '#e5e7eb',
    transform: 'translateX(-50%)',
  },
  event: {
    display: 'flex',
    marginBottom: '24px',
    position: 'relative',
    alignItems: 'flex-start',
  },
  dot: {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    border: '2px solid #fff',
    boxShadow: '0 0 0 2px #e5e7eb',
    zIndex: 1,
    top: '8px',
  },
  dateBox: {
    width: '45%',
    textAlign: 'right',
    paddingRight: '28px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#6b7280',
    paddingTop: '6px',
  },
  contentBox: {
    width: '45%',
    paddingLeft: '28px',
  },
  eventCard: {
    background: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '12px 16px',
  },
  eventHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '6px',
  },
  icon: {
    fontSize: '16px',
    lineHeight: 1,
  },
  description: {
    fontSize: '13px',
    color: '#374151',
    lineHeight: 1.5,
    marginBottom: '8px',
  },
  meta: {
    display: 'flex',
    gap: '12px',
    fontSize: '11px',
    color: '#9ca3af',
  },
  badge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '10px',
    fontSize: '11px',
    fontWeight: 600,
  },
};

export default function ChangeEventTimeline({ events }) {
  if (!events || events.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Price Change Timeline</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No change events detected
        </div>
      </div>
    );
  }

  const sortedEvents = [...events].sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <div style={styles.card}>
      <div style={styles.title}>Price Change Timeline</div>
      <div style={styles.timeline}>
        <div style={styles.line} />
        {sortedEvents.map((event, idx) => {
          const isLeft = idx % 2 === 0;
          const typeColor = TYPE_COLORS[event.type] || '#6b7280';
          const typeIcon = TYPE_ICONS[event.type] || '\u2022';

          const magnitudeColor =
            event.avgMagnitude != null
              ? event.avgMagnitude > 5
                ? '#dc2626'
                : event.avgMagnitude > 1
                ? '#f59e0b'
                : '#16a34a'
              : '#6b7280';

          return (
            <div key={idx} style={styles.event}>
              <div
                style={{
                  ...styles.dot,
                  background: typeColor,
                }}
              />
              {isLeft ? (
                <>
                  <div style={styles.dateBox}>
                    <span>{event.date}</span>
                  </div>
                  <div style={{ width: '10%' }} />
                  <div style={{ ...styles.contentBox, textAlign: 'left' }}>
                    <div style={styles.eventCard}>
                      <div style={styles.eventHeader}>
                        <span style={{ ...styles.icon, color: typeColor }}>{typeIcon}</span>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: typeColor }}>
                          {(event.type || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                        </span>
                      </div>
                      <div style={styles.description}>{event.description}</div>
                      <div style={styles.meta}>
                        {event.affectedAddresses != null && (
                          <span>{event.affectedAddresses} address{event.affectedAddresses !== 1 ? 'es' : ''}</span>
                        )}
                        {event.affectedFields && (
                          <span>{event.affectedFields.join(', ')}</span>
                        )}
                        {event.avgMagnitude != null && (
                          <span
                            style={{
                              ...styles.badge,
                              color: magnitudeColor,
                              background: magnitudeColor + '15',
                            }}
                          >
                            {event.avgMagnitude > 0 ? '+' : ''}
                            {event.avgMagnitude.toFixed(2)}%
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ ...styles.contentBox, width: '45%', textAlign: 'right', paddingRight: '28px', paddingLeft: '0' }}>
                    <div style={styles.eventCard}>
                      <div style={{ ...styles.eventHeader, justifyContent: 'flex-end' }}>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: typeColor }}>
                          {(event.type || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                        </span>
                        <span style={{ ...styles.icon, color: typeColor }}>{typeIcon}</span>
                      </div>
                      <div style={styles.description}>{event.description}</div>
                      <div style={{ ...styles.meta, justifyContent: 'flex-end' }}>
                        {event.affectedAddresses != null && (
                          <span>{event.affectedAddresses} address{event.affectedAddresses !== 1 ? 'es' : ''}</span>
                        )}
                        {event.affectedFields && (
                          <span>{event.affectedFields.join(', ')}</span>
                        )}
                        {event.avgMagnitude != null && (
                          <span
                            style={{
                              ...styles.badge,
                              color: magnitudeColor,
                              background: magnitudeColor + '15',
                            }}
                          >
                            {event.avgMagnitude > 0 ? '+' : ''}
                            {event.avgMagnitude.toFixed(2)}%
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div style={{ width: '10%' }} />
                  <div style={{ ...styles.dateBox, textAlign: 'left', paddingLeft: '28px', paddingRight: '0' }}>
                    <span>{event.date}</span>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
